/**
 * Fullscreen and orientation-lock helpers.
 *
 * The inspection flow runs fullscreen and locked to landscape (see
 * switchLandscape in components/Instructions.js). Screens that are read or filled
 * in rather than aimed at a car -- the document upload, for one -- are easier to
 * use in whichever way the phone is being held, so they release the lock on the
 * way in and restore it on the way out.
 *
 * Every call is best effort and never throws. These APIs are inconsistent: iOS
 * Safari has no screen.orientation.lock at all and only allows fullscreen on a
 * handful of elements, Android needs fullscreen BEFORE a lock will be accepted,
 * and a lock rejects outright unless it happens in a user gesture. A screen must
 * still work when all of it fails, so nothing here is awaited for correctness --
 * the promises are returned only so a caller can sequence if it wants to.
 */

const screenOrientation = () => {
  try {
    return (
      (typeof screen !== "undefined" && screen.orientation) ||
      (window.screen && window.screen.orientation) ||
      null
    );
  } catch (e) {
    return null;
  }
};

/** True while the document is fullscreen, across the vendor prefixes. */
export const isFullscreen = () => {
  try {
    return !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    );
  } catch (e) {
    return false;
  }
};

/**
 * Release the orientation lock and leave fullscreen, so the page follows however
 * the phone is held. Safe to call when neither is active.
 */
export const exitFullscreenAndUnlock = async () => {
  try {
    const o = screenOrientation();
    // unlock() throws synchronously on browsers that do not implement it.
    if (o && typeof o.unlock === "function") {
      try {
        o.unlock();
      } catch (e) {
        /* not supported: the page is simply not locked */
      }
    }
  } catch (e) {
    /* ignore */
  }

  try {
    if (!isFullscreen()) return false;
    const exit =
      document.exitFullscreen ||
      document.webkitExitFullscreen ||
      document.mozCancelFullScreen ||
      document.msExitFullscreen;
    if (!exit) return false;
    await exit.call(document);
    return true;
  } catch (e) {
    // Chrome rejects exitFullscreen when there is nothing to exit; harmless.
    return false;
  }
};

/**
 * Go back to the fullscreen, landscape-locked state the inspection flow expects.
 *
 * Fullscreen is requested first because Android refuses an orientation lock
 * outside it. Both steps are optional: if either is refused the caller carries on,
 * which is what happens on iOS today.
 */
export const enterFullscreenLandscape = async () => {
  try {
    const el = document.documentElement;
    const request =
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.mozRequestFullScreen ||
      el.msRequestFullscreen;
    if (request && !isFullscreen()) {
      try {
        await request.call(el);
      } catch (e) {
        /* refused -- carry on and still try the lock */
      }
    }

    const o = screenOrientation();
    if (o && typeof o.lock === "function") {
      try {
        await o.lock("landscape");
      } catch (e) {
        /* refused, or unsupported: the rotate prompt elsewhere covers it */
      }
    }
    return true;
  } catch (e) {
    return false;
  }
};
